/**
 * IssueSummary Component
 * Displays the summary of items to be issued and destination (transfer or patient)
 */

import type { IssuedItem } from '@/types/index.ts';
import { requiresItemsPerUnit } from '@/types/index.ts';

interface IssueSummaryProps {
  items: IssuedItem[];
  onRemoveItem: (index: number) => void;
  onSubmit: () => void;
  notes: string;
  onNotesChange: (value: string) => void;
  isSubmitting: boolean;
  /** 'location' = transfer to another location, 'patient' = issue to patient */
  issueToType: 'location' | 'patient';
  /** Destination location name when issueToType === 'location' */
  toLocationName?: string;
  /** Patient name when issueToType === 'patient' */
  patientName?: string;
}

export default function IssueSummary({
  items,
  onRemoveItem,
  onSubmit,
  notes,
  onNotesChange,
  isSubmitting,
  issueToType,
  toLocationName,
  patientName,
}: IssueSummaryProps) {
  if (items.length === 0) {
    return null;
  }

  const destinationLabel =
    issueToType === 'location' && toLocationName
      ? `Transfer to: ${toLocationName}`
      : issueToType === 'patient' && patientName?.trim()
        ? `Issue to patient: ${patientName.trim()}`
        : issueToType === 'location'
          ? 'Transfer to: (select location on the left)'
          : 'Issue to patient: (enter patient name on the left)';

  return (
    <div className="mt-6 bg-white rounded-lg border border-gray-200 shadow-sm">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Issue Summary</h3>
        <p className="text-sm text-gray-600 mt-1">{items.length} item(s) to issue</p>
        <p className="text-sm font-medium text-[#0997a4] mt-2 flex items-center gap-1">
          <i className="ri-arrow-right-line" aria-hidden />
          {destinationLabel}
        </p>
      </div>

      <div className="p-6">
        {/* Issue Items */}
        <div className="space-y-3 mb-4">
          {items.map((item, index) => {
            const needsItemsPerUnit = requiresItemsPerUnit(item.unit);
            
            return (
              <div
                key={index}
                className="flex items-start justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{item.name}</p>
                  <div className="mt-1 space-y-1">
                    {item.sku && <p className="text-xs text-gray-600">SKU: {item.sku}</p>}
                    <p className="text-xs text-gray-600">Barcode: {item.barcode}</p>
                    {needsItemsPerUnit && item.packsBoxes && item.itemsPerUnit ? (
                      <>
                        <p className="text-xs text-gray-600">
                          {item.packsBoxes} {item.unit}
                        </p>
                        <p className="text-xs text-gray-600">
                          {item.itemsPerUnit} items per {item.unit}
                        </p>
                        <p className="text-xs font-medium text-gray-900">
                          Total: {item.quantity} items
                        </p>
                      </>
                    ) : (
                      <p className="text-xs text-gray-600">
                        {item.quantity} {item.unit}
                      </p>
                    )}
                    <p className="text-xs text-gray-500">{item.timestamp}</p>
                  </div>
                </div>
                <button
                  onClick={() => onRemoveItem(index)}
                  className="text-red-500 hover:text-red-700 transition-colors p-1"
                  title="Remove item"
                >
                  <i className="ri-delete-bin-line text-lg"></i>
                </button>
              </div>
            );
          })}
        </div>

        {/* Notes */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Notes (Optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            placeholder="Add any notes about this issue..."
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1] resize-none"
          />
        </div>

        {/* Submit Button */}
        <button
          onClick={onSubmit}
          disabled={isSubmitting}
          className="w-full px-4 py-3 bg-[#0997a4] text-white rounded-lg text-sm font-medium hover:bg-[#087d89] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin"></i>
              Processing...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-check-line"></i>
              Complete Issue
            </span>
          )}
        </button>
      </div>
    </div>
  );
}

